import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import CartIcon from '../../components/CartIcon'
import ClienteLayout from '../../components/ClienteLayout'
import ProductosDestacadosCarrusel from '../../components/ProductosDestacadosCarrusel'
import TrustBadge from '../../components/TrustBadge'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import {
  getMisSuscripciones, getDashboardResumen, getAhorroAcumulado, getProximoCobro,
} from '../../lib/portalClienteApi'
import { rangoHorarioVentana } from '../../lib/ventanasHorarias'

// Fallback estático si falla la generación con IA
const TIPS_FALLBACK = [
  { icono: '🌿', titulo: 'El bicarbonato es tu aliado', texto: 'Una cucharada en el lavarropas refuerza el detergente y neutraliza olores.' },
  { icono: '🍋', titulo: 'Limón para grasa', texto: 'Mezclá jugo de limón con bicarbonato para quitar grasa de la cocina sin químicos fuertes.' },
  { icono: '🧴', titulo: 'Vinagre + agua', texto: 'Mitad vinagre blanco, mitad agua. El multiuso natural más barato.' },
  { icono: '📦', titulo: 'Guardá los envases', texto: 'Cuando termina un producto, lavalo y dejalo airear. Reutilizable para mil cosas.' },
]

const SALUDOS = ['Buenos días', 'Buenas tardes', 'Buenas noches']

const TIER_LINEA = {
  essential: 'Línea Essential',
  premium:   'Línea Premium',
  comercial: 'Línea Comercial',
}

// Saludo dinámico por hora local del navegador.
// Cortes naturales para Montevideo: 5am-12 = días, 12-19 = tardes, 19-5am = noches.
// Antes el corte era 0-13 → si entrabas a la 1am decía "Buenos días" (raro).
function getSaludo() {
  const h = new Date().getHours()
  if (h >= 5 && h < 12) return SALUDOS[0]    // 5am - 11:59 — Buenos días
  if (h >= 12 && h < 19) return SALUDOS[1]   // 12:00 - 18:59 — Buenas tardes
  return SALUDOS[2]                           // 19:00 - 4:59 — Buenas noches
}

// Regla cobro: D-1 a las 16hs, con snap a viernes si cae sábado o domingo.
// Comunicado en NuevaSuscripcion.jsx Paso 5 y Soporte.jsx FAQ.
function diaAntesDe(iso) {
  if (!iso) return null
  const d = new Date(iso + (iso.length === 10 ? 'T12:00:00' : ''))
  d.setDate(d.getDate() - 1)
  // 0=domingo, 6=sábado → retroceder hasta viernes
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() - 1)
  }
  return d.toISOString().slice(0, 10)
}

function fmtFecha(iso) {
  if (!iso) return null
  const d = new Date(iso + (iso.length === 10 ? 'T12:00:00' : ''))
  const dias = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  return `${dias[d.getDay()]} ${d.getDate()} ${meses[d.getMonth()]}`
}

// Fix 7-jul-2026: helpers para mostrar el patrón mensual que eligió el cliente
// ej: "2do jueves de cada mes"
const SEMANA_ORDINAL = { 1: '1er', 2: '2do', 3: '3er', 4: '4to' }
const DIA_LABEL_FULL = {
  lunes: 'lunes', martes: 'martes', miercoles: 'miércoles',
  jueves: 'jueves', viernes: 'viernes', sabado: 'sábado', domingo: 'domingo',
}
function fmtPatronMensual(diaEntrega, semanaMes) {
  if (!diaEntrega || !semanaMes) return null
  const ord = SEMANA_ORDINAL[semanaMes] || `${semanaMes}º`
  const dia = DIA_LABEL_FULL[diaEntrega] || diaEntrega
  return `${ord} ${dia} de cada mes`
}

function fmtMonto(n) {
  return '$' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })
}

export default function Inicio() {
  const { user, profile } = useAuth()
  const [suscripciones, setSuscripciones] = useState([])
  const [resumen, setResumen] = useState(null)
  const [ahorro, setAhorro] = useState(null)
  const [tipDia, setTipDia] = useState(null)  // tip diario IA + producto
  const [cargando, setCargando] = useState(true)
  // Vecinos en mi cuadra — banner personalizado del Descuento Colaborativo.
  // Nico 04-jun-2026: si tenemos dirección del user, mostrar cuántos vecinos
  // ya están suscriptos y cuántos faltan para llegar al 5% off.
  const [vecinosCuadra, setVecinosCuadra] = useState(null)
  // Fix 7-jul-2026: mostrar último pago exitoso + próximo con fechas concretas
  const [ultimoPago, setUltimoPago] = useState(null)
  // Fix 7-jul-2026: fecha REAL del próximo cobro (D-1 del próximo jueves/día de la 2da semana del mes siguiente)
  const [proximoCobro, setProximoCobro] = useState(null)

  useEffect(() => {
    if (!user?.id) return
    cargar()
    cargarTip()
    cargarVecinosCuadra()
  }, [user?.id])

  // Fix 7-jul-2026: traer último pago de la suscripción (pagado o fallido) para mostrar estado con fecha
  async function cargarUltimoPago(suscripcionId) {
    if (!suscripcionId) return
    try {
      const { data } = await supabase.from('pagos')
        .select('id, monto, estado, pagado_at, vence, mes')
        .eq('cliente_id', suscripcionId)
        .in('estado', ['pagado', 'fallido', 'pendiente'])
        .order('pagado_at', { ascending: false, nullsFirst: false })
        .order('vence', { ascending: false })
        .limit(1)
        .maybeSingle()
      setUltimoPago(data || null)
    } catch (e) { console.warn('cargarUltimoPago:', e?.message) }
  }

  async function cargarVecinosCuadra() {
    try {
      const { data, error } = await supabase.rpc('cliente_vecinos_en_mi_cuadra')
      if (error) { console.warn('cliente_vecinos_en_mi_cuadra:', error.message); return }
      // RPC devuelve un array con una sola fila
      const row = Array.isArray(data) ? data[0] : data
      if (row) setVecinosCuadra(row)
    } catch (e) {
      console.warn('cliente_vecinos_en_mi_cuadra error:', e.message)
    }
  }

  // Realtime: refrescar si cambia mi suscripción, mi kit o algún producto del kit
  useEffect(() => {
    if (!user?.id) return
    const kitCodigo = suscripciones[0]?.kit_actual
    if (!kitCodigo) return
    let t
    const debounced = () => { clearTimeout(t); t = setTimeout(cargar, 600) }
    const ch = supabase
      .channel(`inicio:${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'kit_items', filter: `kit_codigo=eq.${kitCodigo}` }, debounced)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'kits', filter: `codigo=eq.${kitCodigo}` }, debounced)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'suscripciones', filter: `cliente_id=eq.${user.id}` }, debounced)
      .subscribe()
    return () => { clearTimeout(t); supabase.removeChannel(ch) }
  }, [user?.id, suscripciones[0]?.kit_actual])

  async function cargar() {
    setCargando(true)
    const [ss, ah] = await Promise.all([
      getMisSuscripciones(user.id),
      getAhorroAcumulado(user.id),
    ])
    setSuscripciones(ss)
    setAhorro(ah)
    if (ss[0]) {
      const r = await getDashboardResumen(ss[0].id)
      setResumen(r)
      cargarUltimoPago(ss[0].id)
      // Fix 7-jul-2026: fecha real del próximo cobro (backend calcula con semana_mes + dia_entrega)
      getProximoCobro(ss[0].id).then(setProximoCobro)
    }
    setCargando(false)
  }

  async function cargarTip() {
    // 1) Intentamos leer el tip de hoy desde la tabla
    let { data, error } = await supabase.rpc('get_tip_hoy')
    if (error) { console.error('get_tip_hoy:', error); return }
    let tip = data?.[0]

    // 2) Si no hay tip de hoy, lo generamos llamando a la edge function
    if (!tip) {
      try {
        const { data: gen } = await supabase.functions.invoke('generar-tip-diario', { body: {} })
        if (gen?.success && gen?.tip) {
          // Re-leemos para obtener los datos del producto joineados
          const { data: data2 } = await supabase.rpc('get_tip_hoy')
          tip = data2?.[0]
        }
      } catch (e) {
        console.error('generar-tip-diario:', e)
      }
    }

    setTipDia(tip || null)
  }

  // Solo contamos como "suscriptor activo" a quienes tienen una susc en estado
  // activa (con preapproval authorized) o pausada. Las canceladas no muestran UI de suscriptor.
  // Fix #12 (7-jul-2026): estado 'activa' ahora requiere preapproval authorized (el webhook lo promueve).
  const suscActiva = suscripciones.find(s =>
    (s.estado === 'activa' && s.mp_preapproval_status === 'authorized') || s.estado === 'pausada'
  )
  const tieneSuscripcion = !!suscActiva
  const susc = suscActiva || suscripciones[0]

  // La suscripción está "esperando pago" en 2 casos:
  //   (a) estado 'pendiente_tarjeta' — Fix #12: la RPC arranca en este estado, el webhook la promueve.
  //   (b) estado 'activa' pero mp_preapproval_status != 'authorized' — legacy pre-fix #12.
  const suscPagoPendiente = suscripciones.find(s =>
    s.estado === 'pendiente_tarjeta'
    || (s.estado === 'activa' && s.mp_preapproval_status !== 'authorized')
  )
  const esperandoPago = !!suscPagoPendiente
  const idParaCompletarPago = suscPagoPendiente?.id || suscActiva?.id
  // Kit reservado — mostramos nombre y precio en el banner de pago pendiente
  // Nico 8-jul-2026: 'debería decir tenés reservado un Kit, hasta que no autorices el pago no queda activo'.
  const kitReservado = suscPagoPendiente?.kit
  const kitReservadoNombre = kitReservado?.nombre || 'tu kit'
  const kitReservadoPrecio = Number(kitReservado?.precio_susc || 0)
  const proximaFecha = resumen?.proximaEntregaReal || resumen?.proximaEntregaCalc
  // Nuevo modelo (post-Roberto, 9-jul-2026): MP cobra cada 30 días desde el último pago.
  // Si aún no tenemos next_payment_date guardado en clientes, lo derivamos de ultimoPago.pagado_at + 30d.
  const proximoCobroFecha = ultimoPago?.pagado_at
    ? new Date(new Date(ultimoPago.pagado_at).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    : null
  const proximoPago = resumen?.proximoPago
  // Fix bug 7-jul-2026: la RPC devuelve 'ahorro_total', el frontend leía 'ahorro_total_anio'. Naming mismatch → siempre daba $0.
  const ahorroAnio = ahorro?.ahorro_total || ahorro?.ahorro_total_anio || 0
  const nombre = (profile?.nombre || user?.email || '').split(' ')[0].split('@')[0]
  const tipFallback = TIPS_FALLBACK[new Date().getDate() % TIPS_FALLBACK.length]
  const tip = tipDia || tipFallback
  const tipTieneProducto = tipDia?.producto_sku && tipDia?.producto_imagen_url

  // Bonificación 3 meses (devolución del mes 1 repartido en 2 y 3)
  const bonif = resumen?.bonif
  const bonifAplica = bonif?.aplica
  const m1 = Number(bonif?.montoFull || 0)
  const precioNormal = Number(bonif?.montoNormal || 0)
  const m23 = precioNormal - Number(bonif?.ahorroTotal || 0) / 2
  const total3meses = m1 + m23 * 2

  // Pago del mes siguiente (sobre la suscripción): si está en bonif mes 1 o 2 → m23 (mes 2/3 con descuento), si mes 3 → precio normal del mes 4, si no hay bonif → precio normal
  const pagoMesSig = bonifAplica
    ? (Number(bonif?.mesActual) >= 3 ? precioNormal : m23)
    : precioNormal

  // Ahorro de este mes: aproximación simple (anual / meses transcurridos del año)
  const _mesActual = new Date().getMonth() + 1
  const ahorroMes = ahorroAnio > 0 ? Math.round(Number(ahorroAnio) / _mesActual) : 0

  return (
    <ClienteLayout>
      <div className="i-wrap">
        {/* Hero saludo — Nico 04-jun-2026: 'Buenos días/tardes/noches automático
            por hora, sin el "Hola" duplicado, H1 más chico, sacar subtítulo'.
            Solo mostramos subtítulo si tiene suscripción (info útil sobre próxima
            entrega); si no tiene, lo sacamos porque el banner 'Sumate a Limpack'
            ya cumple la función. */}
        <div className="i-hero">
          <div className="i-greet">{getSaludo()},</div>
          <h1 className="i-hello"><em>{nombre}</em> 👋</h1>
          {tieneSuscripcion && (
            <p className="i-subhello">Tu próxima entrega ya está armada. Mirá qué viene o sumá productos.</p>
          )}
        </div>

        {/* Banner: falta pagar para activar la suscripción */}
        {esperandoPago && idParaCompletarPago && (
          <Link to={`/cliente/cargar-tarjeta/${idParaCompletarPago}`} className="i-pago-pendiente">
            <span className="i-pago-pendiente-icon">📦</span>
            <div className="i-pago-pendiente-body">
              <strong>
                Tenés reservado el <em>{kitReservadoNombre}</em>
                {kitReservadoPrecio > 0 && <> · ${kitReservadoPrecio.toLocaleString('es-UY')}/mes</>}
              </strong>
              <span>Autorizá el cobro con tu tarjeta para activarlo. Hasta que no entre el pago, no queda en la ruta de entrega.</span>
            </div>
            <span className="i-pago-pendiente-cta">Autorizar pago →</span>
          </Link>
        )}

        {/* Rediseño Bento 7-jul-2026 — Nico: 'espacios desaprovechados, Bonificación queda perdida'.
            Orden nuevo: (1) CTA sumate si no tiene susc, sino (2) HERO Bonificación arriba (destacada),
            (3) Fila 3 métricas densas (Entrega + Pago + Ahorro), (4) 2 acciones balanceadas. */}

        {/* CTA "Sumate" solo si NO tiene suscripción ni pago pendiente */}
        {!tieneSuscripcion && !esperandoPago && !cargando && (
          <Link to="/cliente/nueva-suscripcion" className="i-strip i-strip-cta">
            <span className="i-strip-icon">✨</span>
            <div className="i-strip-info">
              <div className="i-strip-label">Empezá hoy</div>
              <div className="i-strip-title">Sumate a Limpack — elegí tu kit</div>
              <div className="i-strip-sub">Kits pensados para tu hogar · sin permanencia</div>
            </div>
            <span className="i-strip-cta-btn">Empezar →</span>
          </Link>
        )}

        {/* HERO: Bonificación 3 meses — protagonista arriba */}
        {tieneSuscripcion && bonifAplica && (
          <div className="i-bento-bonif">
            <div className="i-bento-bonif-icon-bg">🎁</div>
            <div className="i-bento-bonif-top">
              <div>
                <div className="i-bento-bonif-tag">🎁 BONIFICACIÓN 3 MESES</div>
                <div className="i-bento-bonif-titulo">Estás en el mes {bonif.mesActual} de 3</div>
                <div className="i-bento-bonif-sub">
                  {bonif.mesActual === 1 && <>Ahorrás ${(bonif.ahorroTotal).toLocaleString('es-UY')} repartidos en meses 2 y 3</>}
                  {bonif.mesActual === 2 && <>Esta factura ya viene con ${(bonif.ahorroTotal/2).toLocaleString('es-UY')} de descuento</>}
                  {bonif.mesActual === 3 && <>Última con descuento. Desde el mes 4 pagás el precio normal</>}
                </div>
              </div>
              <div className="i-bento-bonif-prom">
                <div className="i-bento-bonif-prom-tag">PROMEDIO</div>
                <div className="i-bento-bonif-prom-valor">${Math.round(total3meses/3).toLocaleString('es-UY')}/mes</div>
              </div>
            </div>
            <div className="i-bento-bonif-bar">
              <div className="i-bento-bonif-bar-fill" style={{ width: `${(bonif.mesActual/3)*100}%` }}/>
            </div>
            <div className="i-bento-bonif-meses">
              <span className={bonif.mesActual === 1 ? 'actual' : bonif.mesActual > 1 ? 'done' : ''}>Mes 1 · ${m1.toLocaleString('es-UY')}</span>
              <span className={bonif.mesActual === 2 ? 'actual' : bonif.mesActual > 2 ? 'done' : ''}>Mes 2 · ${m23.toLocaleString('es-UY')}</span>
              <span className={bonif.mesActual === 3 ? 'actual' : ''}>Mes 3 · ${m23.toLocaleString('es-UY')}</span>
            </div>
          </div>
        )}

        {/* Banner explicativo del patrón mensual que eligió el cliente.
            Fix 7-jul-2026: le da tranquilidad ver cuándo llegan sus kits sin tener que recordar. */}
        {tieneSuscripcion && susc?.dia_entrega && susc?.semana_mes && (
          <div className="i-bento-patron">
            <span className="i-bento-patron-icon">📅</span>
            <div className="i-bento-patron-text">
              Recibís tu kit el <b>{fmtPatronMensual(susc.dia_entrega, susc.semana_mes)}</b>
              <span className="i-bento-patron-sep">·</span>
              <span className="i-bento-patron-sub">Cobramos el día anterior</span>
            </div>
          </div>
        )}

        {/* FILA 3 métricas densas: Entrega + Pago + Ahorro */}
        {tieneSuscripcion && (
          <div className="i-bento-metricas">
            <Link to="/cliente/mi-plan" className="i-bento-metrica">
              <div className="i-bento-metrica-head">📦 PRÓXIMA ENTREGA</div>
              <div className="i-bento-metrica-valor">{fmtFecha(proximaFecha)}</div>
              <div className="i-bento-metrica-detalle">🕐 {rangoHorarioVentana(susc.ventana_horaria).replace(' - ', '–')}hs</div>
              <div className="i-bento-metrica-detalle">
                {susc.kit?.nombre || 'Tu kit'}
                {susc.barrio ? ` · ${susc.barrio}` : ''}
              </div>
            </Link>

            {/* Fix 7-jul-2026: card con estado real del pago (último exitoso + próximo con fecha).
                Antes solo mostraba monto abstracto sin decir si el user tenía deuda o no. */}
            <Link to="/cliente/pedidos" className="i-bento-metrica">
              <div className="i-bento-metrica-head">💳 SUSCRIPCIÓN MENSUAL</div>
              {ultimoPago?.estado === 'pagado' ? (
                <>
                  <div className="i-bento-metrica-badge success" style={{ fontSize: 12.5, marginBottom: 2 }}>
                    ✓ Pagaste {fmtMonto(ultimoPago.monto)}
                  </div>
                  <div className="i-bento-metrica-detalle">
                    el {new Date(ultimoPago.pagado_at).toLocaleDateString('es-UY', { day: 'numeric', month: 'short' })}
                  </div>
                  <div className="i-bento-metrica-detalle" style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--cl-line)' }}>
                    Próximo: {fmtMonto(pagoMesSig)}
                    {proximoCobro?.fecha_cobro && ` · ${fmtFecha(proximoCobro.fecha_cobro)}`}
                  </div>
                </>
              ) : ultimoPago?.estado === 'fallido' ? (
                <>
                  <div className="i-bento-metrica-valor" style={{ color: '#f87171' }}>{fmtMonto(ultimoPago.monto)}</div>
                  <div className="i-bento-metrica-detalle" style={{ color: '#f87171', fontWeight: 700 }}>
                    ⚠ Cobro fallido · Actualizá tu tarjeta
                  </div>
                </>
              ) : proximoPago ? (
                <>
                  <div className="i-bento-metrica-valor">{fmtMonto(proximoPago.monto)}</div>
                  <div className="i-bento-metrica-detalle">
                    Próximo cobro {proximoCobroFecha ? fmtFecha(proximoCobroFecha) : 'a los 30 días'}
                  </div>
                  {susc?.mp_preapproval_status === 'authorized' ? (
                    <div className="i-bento-metrica-badge success">🔒 Débito MP automático</div>
                  ) : (
                    <div className="i-bento-metrica-detalle">Precios c/IVA</div>
                  )}
                </>
              ) : (
                <>
                  <div className="i-bento-metrica-valor">{fmtMonto(pagoMesSig)}</div>
                  <div className="i-bento-metrica-detalle">Próxima cuota</div>
                  {susc?.mp_preapproval_status === 'authorized' && (
                    <div className="i-bento-metrica-badge success">🔒 Débito MP automático</div>
                  )}
                </>
              )}
            </Link>

            <Link to="/cliente/pedidos" className="i-bento-metrica">
              <div className="i-bento-metrica-head">💰 AHORRADO ESTE AÑO</div>
              <div className="i-bento-metrica-valor success">{fmtMonto(ahorroAnio)}</div>
              <div className="i-bento-metrica-detalle">vs promedio del mercado</div>
              {ahorroMes > 0 && (
                <div className="i-bento-metrica-detalle">Este mes: {fmtMonto(ahorroMes)}</div>
              )}
            </Link>
          </div>
        )}

        {/* Rediseño 8-jul-2026 (Nico): Tienda + Vecinos + Tip en grid auto-fit
            para que aproveche el ancho en desktop y no se vean vacías las cards. */}
        <div className="i-secundarios-grid">
          {/* La Tienda Limpack — card del grid con carrusel de destacados adentro.
              Antes era Link chico horizontal; ahora es container vertical:
              header (clickeable) + carrusel compacto + CTA "Ver todos →". */}
          <div className="i-bento-tienda">
            <Link to="/catalogo" className="i-bento-tienda-head">
              <div className="i-bento-accion-icon"><CartIcon size={22} strokeWidth={2.2} /></div>
              <div className="i-bento-accion-body">
                <div className="i-bento-accion-titulo">La Tienda Limpack</div>
                <div className="i-bento-accion-sub">Productos sueltos · llegan el día de tu ruta</div>
              </div>
            </Link>
            <ProductosDestacadosCarrusel />
            <Link to="/catalogo" className="i-bento-tienda-cta">
              Ver todos <span>→</span>
            </Link>
          </div>
          {/* Descuento colaborativo — copy CONTEXTUAL según vecinosCuadra:
              - tieneSuscripcion + ya_descuento_activo → 'tu cuadra ya tiene el 5%'
              - tieneSuscripcion + parcial → 'sos parte de los N, faltan X más'
              - !tieneSuscripcion + dirección + vecinos > 0 → 'en tu cuadra ya hay N, si te sumás...'
              - !tieneSuscripcion + dirección + vecinos = 0 → 'sé el primero de tu cuadra'
              - sin dirección → mensaje genérico */}
          {(() => {
            const tieneDir = vecinosCuadra?.tiene_direccion
            const vActivos = vecinosCuadra?.vecinos_activos || 0
            const meta = vecinosCuadra?.meta_vecinos || 10
            const yaActivo = vecinosCuadra?.ya_descuento_activo
            // Capitalizar calle: 'rivera' → 'Rivera', 'av. libertador' → 'Av. Libertador'
            // Fix 7-jul-2026: bug #8 (nombre en minúscula) + copy engañoso "En rivera" (calle
            // larga) — el descuento es CUADRA por CUADRA, no calle entera. Nueva forma:
            // "En tu cuadra de Rivera".
            const calleRaw = vecinosCuadra?.calle || ''
            const calle = calleRaw.split(' ').map(w => w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : '').join(' ')
            const barrio = vecinosCuadra?.barrio_nombre

            let titulo, desc, ctaPrimary, ctaPrimaryLabel
            ctaPrimary = '/cliente/vecinos'
            ctaPrimaryLabel = 'Ver mis vecinos →'

            if (tieneDir && yaActivo) {
              titulo = '¡Tu cuadra ya tiene el 5%! 🎉'
              desc = <>En <strong>tu cuadra de {calle}</strong>{barrio ? ` (${barrio})` : ''} ya hay <strong>{vActivos} {vActivos === 1 ? 'vecino suscripto' : 'vecinos suscriptos'}</strong> a un kit Limpack y todos disfrutan <strong>5% off permanente en su kit mensual</strong>. Cuando te suscribas a un kit, vos también.</>
            } else if (tieneDir && vActivos > 0 && !tieneSuscripcion) {
              const faltan = Math.max(0, meta - vActivos - 1)
              if (faltan === 0) {
                titulo = '¡Sos el que falta! 🤝'
                desc = <>En <strong>tu cuadra de {calle}</strong>{barrio ? ` (${barrio})` : ''} ya hay <strong>{vActivos} {vActivos === 1 ? 'vecino suscripto' : 'vecinos suscriptos'}</strong> a un kit. Si te sumás a un kit, llegan a {meta} y <strong>todos ganan 5% off permanente en la suscripción</strong>.</>
                ctaPrimary = '/cliente/nueva-suscripcion'
                ctaPrimaryLabel = 'Elegir mi kit →'
              } else {
                titulo = `Tu cuadra está a ${faltan + 1} de ganar 5% off 🤝`
                desc = <>En <strong>tu cuadra de {calle}</strong>{barrio ? ` (${barrio})` : ''} hay <strong>{vActivos} {vActivos === 1 ? 'vecino suscripto' : 'vecinos suscriptos'}</strong> a un kit. Si te sumás, faltan solo <strong>{faltan} más</strong> para que todos ganen <strong>5% off permanente en su kit mensual</strong>.</>
                ctaPrimary = '/cliente/nueva-suscripcion'
                ctaPrimaryLabel = 'Elegir mi kit →'
              }
            } else if (tieneDir && vActivos === 0 && !tieneSuscripcion) {
              titulo = 'Sé el primero de tu cuadra 🤝'
              desc = <>Si juntás <strong>{meta} vecinos en tu cuadra de {calle}</strong>{barrio ? ` (${barrio})` : ''} suscriptos a un kit Limpack, todos ganan <strong>5% off permanente en la suscripción</strong>. Empezá con vos.</>
              ctaPrimary = '/cliente/nueva-suscripcion'
              ctaPrimaryLabel = 'Elegir mi kit →'
            } else if (tieneSuscripcion && vActivos > 0 && !yaActivo) {
              const faltan = Math.max(0, meta - vActivos - 1)
              titulo = `Falta poco para el 5% en tu cuadra 🤝`
              desc = <>Sos parte de los <strong>{vActivos + 1} vecinos</strong> con kit en {calle}. Si <strong>{faltan} más</strong> se suscriben a un kit, todos ganan <strong>5% off permanente en la suscripción</strong>.</>
            } else {
              // Sin dirección — Nico 04-jun-2026: el cliente puede estar
              // logueado sin suscripción ni pedido (solo creó cuenta). Antes
              // dependía de comprar/suscribirse para activar Vecinos —
              // ahora el CTA principal carga la dirección suelta y queda
              // habilitado el descuento colaborativo sin comprometerse.
              titulo = '¿Tu cuadra ya tiene el 5%? 🤝'
              desc = <>Cargá tu dirección y te decimos cuántos vecinos hay suscriptos a un kit en tu cuadra. Si llegan a <strong>{meta}</strong>, todos ganan <strong>5% off permanente en su kit mensual</strong>. Sin compromiso de compra.</>
              ctaPrimary = '/cliente/mi-direccion'
              ctaPrimaryLabel = 'Cargar mi dirección →'
            }

            return (
              <div className="i-vecinos-card">
                <div className="i-vecinos-emoji">🤝</div>
                <div className="i-vecinos-body">
                  <div className="i-vecinos-pill">Colaborativo · 5% off</div>
                  <div className="i-vecinos-title">{titulo}</div>
                  <div className="i-vecinos-desc">{desc}</div>
                  <div className="i-vecinos-actions">
                    <Link to={ctaPrimary} className="i-vecinos-btn primary">
                      {ctaPrimaryLabel}
                    </Link>
                    {tieneDir ? (
                      // Con dirección → WhatsApp para invitar vecinos
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(
                          calle
                            ? `Hola! Me sumé a Limpack, me traen los productos de limpieza a casa cada mes a mejor precio que el promedio del mercado. Si juntamos 10 vecinos en ${calle} con kit suscripto, todos ganamos un 5% de descuento permanente. ¿Te sumás? https://limpack.uy`
                            : `Hola! Me sumé a Limpack, me traen los productos de limpieza a casa cada mes a mejor precio que el promedio del mercado. Si juntamos 10 vecinos en la cuadra con kit suscripto, todos ganamos un 5% de descuento permanente. ¿Te sumás? https://limpack.uy`
                        )}`}
                        target="_blank" rel="noopener noreferrer"
                        className="i-vecinos-btn wa"
                      >
                        💬 WhatsApp
                      </a>
                    ) : (
                      // Sin dirección → camino alternativo "compra puntual"
                      // (también captura dirección al hacer el pedido).
                      <Link to="/catalogo" className="i-vecinos-btn wa" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <CartIcon size={16} /> Comprar puntual
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Tip Limpack — más compacto en grid 2 cols */}
          <div className="i-tip-wrap">
            <h2 className="i-tip-h2">Tip Limpack 💡</h2>
            {tipTieneProducto ? (
              <Link to={`/catalogo?sku=${tipDia.producto_sku}`} className="i-tip i-tip-prod">
                <div className="i-tip-icon">{tip.icono || '💡'}</div>
                <div className="i-tip-text">
                  <b>{tip.titulo}.</b> {tip.texto}
                </div>
                <div className="i-tip-prod-img">
                  <img src={tipDia.producto_imagen_url} alt={tipDia.producto_nombre} />
                </div>
              </Link>
            ) : (
              <div className="i-tip">
                <div className="i-tip-icon">{tip.icono || tip.icon || '💡'}</div>
                <div className="i-tip-text">
                  <b>{tip.titulo}.</b> {tip.texto}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Trust badge al final como footer de confianza */}
        <div className="i-trust">
          <TrustBadge variant="default" />
        </div>
      </div>

      <style>{`
        /* Inicio cliente — dark mode estilo landing pública.
           Nico 04-jun-2026. Todos los var(--cl-navy) que eran color de texto
           sobre fondo blanco pasaron a var(--cl-text) (blanco sobre dark).
           Los gradients claros pasaron a glass o se mantuvieron como acentos
           coloridos sobre dark. */
        .i-wrap { max-width: 1180px; margin: 0 auto; padding: 16px 16px 32px }

        .i-hero { padding: 4px 4px 12px }
        .i-greet { font-size: 13px; color: var(--cl-muted); font-weight: 600 }
        /* H1 reducido (Nico 04-jun: '44px desktop era exagerado') */
        .i-hello { font-size: 26px; font-weight: 900; color: var(--cl-text); letter-spacing: -.02em; margin-top: 2px; line-height: 1.1 }
        .i-hello em { color: var(--cl-teal); font-style: normal }
        .i-subhello { font-size: 14px; color: var(--cl-muted); margin-top: 6px; line-height: 1.5 }

        .i-top-grid { display: grid; grid-template-columns: 1fr; gap: 14px; margin-bottom: 8px }

        /* ═══════════════════════════════════════════════════════════════
           BENTO LAYOUT (Nico 7-jul-2026): Bonificación hero + 3 métricas densas
           + acciones balanceadas. Reemplaza .i-top-grid + .i-cuenta + .i-bonif.
           ═══════════════════════════════════════════════════════════════ */

        /* HERO Bonificación — gradient navy→teal, protagonista */
        .i-bento-bonif {
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, var(--cl-navy) 0%, var(--cl-teal) 100%);
          border-radius: 16px;
          padding: 20px 24px;
          margin-bottom: 14px;
          color: #fff;
        }
        .i-bento-bonif-icon-bg {
          position: absolute;
          top: -20px;
          right: -10px;
          font-size: 140px;
          opacity: 0.08;
          pointer-events: none;
        }
        .i-bento-bonif-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 14px;
          position: relative;
        }
        .i-bento-bonif-tag {
          font-size: 11px;
          letter-spacing: 0.1em;
          color: rgba(255,255,255,0.75);
          font-weight: 800;
          margin-bottom: 6px;
        }
        .i-bento-bonif-titulo {
          font-size: 22px;
          font-weight: 900;
          color: #fff;
          letter-spacing: -.01em;
          line-height: 1.2;
          margin-bottom: 4px;
        }
        .i-bento-bonif-sub {
          font-size: 13px;
          color: rgba(255,255,255,0.85);
          line-height: 1.4;
        }
        .i-bento-bonif-prom { text-align: right; flex-shrink: 0 }
        .i-bento-bonif-prom-tag {
          font-size: 10px;
          letter-spacing: 0.1em;
          color: rgba(255,255,255,0.75);
          font-weight: 800;
        }
        .i-bento-bonif-prom-valor {
          font-size: 22px;
          font-weight: 900;
          color: #fff;
          letter-spacing: -.01em;
          margin-top: 2px;
        }
        .i-bento-bonif-bar {
          background: rgba(255,255,255,0.15);
          border-radius: 999px;
          height: 8px;
          overflow: hidden;
          margin-bottom: 10px;
          position: relative;
        }
        .i-bento-bonif-bar-fill {
          background: #fff;
          height: 100%;
          border-radius: 999px;
          transition: width 0.3s;
        }
        .i-bento-bonif-meses {
          display: flex;
          justify-content: space-between;
          gap: 8px;
          font-size: 12px;
          color: rgba(255,255,255,0.75);
          flex-wrap: wrap;
          position: relative;
        }
        .i-bento-bonif-meses .actual { color: #fff; font-weight: 800 }
        .i-bento-bonif-meses .done { color: rgba(255,255,255,0.6) }

        /* Banner patrón mensual (arriba de las 3 métricas) */
        .i-bento-patron {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: rgba(0,212,224,0.06);
          border: 1px solid rgba(0,212,224,0.15);
          border-radius: 10px;
          margin-bottom: 12px;
          color: var(--cl-text);
          font-size: 13px;
        }
        .i-bento-patron-icon {
          font-size: 18px;
          flex-shrink: 0;
        }
        .i-bento-patron-text {
          flex: 1;
          line-height: 1.4;
        }
        .i-bento-patron-text b {
          color: var(--cl-teal);
          font-weight: 800;
        }
        .i-bento-patron-sep {
          margin: 0 8px;
          color: var(--cl-muted);
        }
        .i-bento-patron-sub {
          color: var(--cl-muted);
          font-size: 12.5px;
        }
        @media (max-width: 480px) {
          .i-bento-patron { font-size: 12px; padding: 9px 12px }
          .i-bento-patron-sep { margin: 0 6px }
        }

        /* 3 métricas densas: Entrega + Pago + Ahorro */
        .i-bento-metricas {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
          margin-bottom: 14px;
        }
        @media (min-width: 720px) {
          .i-bento-metricas { grid-template-columns: 1fr 1fr 1fr }
        }
        .i-bento-metrica {
          background: var(--cl-card);
          border: 1px solid var(--cl-line);
          border-radius: 12px;
          padding: 14px 16px;
          text-decoration: none;
          color: var(--cl-text);
          display: flex;
          flex-direction: column;
          gap: 4px;
          transition: border-color .15s, background .15s, transform .15s;
        }
        .i-bento-metrica * { text-decoration: none }
        .i-bento-metrica:hover {
          border-color: var(--cl-teal);
          background: var(--cl-card-hover);
          transform: translateY(-2px);
        }
        .i-bento-metrica-head {
          font-size: 10.5px;
          letter-spacing: 0.08em;
          color: var(--cl-muted);
          font-weight: 800;
          margin-bottom: 4px;
        }
        .i-bento-metrica-valor {
          font-size: 20px;
          font-weight: 900;
          color: var(--cl-text);
          letter-spacing: -.02em;
          line-height: 1.1;
        }
        .i-bento-metrica-valor.success { color: var(--cl-green, #4ade80) }
        .i-bento-metrica-detalle {
          font-size: 12px;
          color: var(--cl-muted);
          line-height: 1.4;
        }
        .i-bento-metrica-badge {
          font-size: 11.5px;
          padding: 3px 0;
          margin-top: 2px;
          font-weight: 700;
        }
        .i-bento-metrica-badge.success { color: var(--cl-green, #4ade80) }

        /* Acción Tienda destacada — desde 8-jul-2026 va dentro de .i-secundarios-grid
           (junto con Vecinos + Tip). height:100% para alinear con las otras cards del grid. */
        .i-bento-accion {
          background: linear-gradient(135deg, var(--cl-bg-2), var(--cl-navy));
          border: 1px solid rgba(0,212,224,.20);
          border-radius: 16px;
          padding: 18px;
          display: flex;
          align-items: center;
          gap: 14px;
          text-decoration: none;
          color: #fff;
          transition: transform .15s, border-color .15s, box-shadow .15s;
          height: 100%;
        }
        .i-bento-accion:hover {
          transform: translateY(-2px);
          border-color: rgba(0,212,224,.40);
          box-shadow: 0 12px 28px rgba(0,212,224,.15);
        }
        .i-bento-accion-icon {
          width: 44px; height: 44px;
          background: rgba(0,212,224,0.15);
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 22px;
          flex-shrink: 0;
        }
        .i-bento-accion-body { flex: 1; min-width: 0 }
        .i-bento-accion-titulo {
          font-size: 15px;
          font-weight: 900;
          color: #fff;
          margin-bottom: 2px;
        }
        .i-bento-accion-sub {
          font-size: 12.5px;
          color: rgba(255,255,255,.75);
          line-height: 1.4;
        }
        .i-bento-accion-arrow {
          font-size: 22px;
          color: var(--cl-teal);
          flex-shrink: 0;
        }

        /* Card Tienda con carrusel embebido (2026-07-09).
           Mismo gradient/border/radius que .i-bento-accion pero layout vertical:
           header clickeable + carrusel compacto + CTA. Height matches i-vecinos-card
           / i-tip para que el grid quede prolijo. */
        .i-bento-tienda {
          background: linear-gradient(135deg, var(--cl-bg-2), var(--cl-navy));
          border: 1px solid rgba(0,212,224,.20);
          border-radius: 16px;
          padding: 14px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          height: 100%;
          box-sizing: border-box;
          transition: border-color .15s, box-shadow .15s;
        }
        .i-bento-tienda:hover {
          border-color: rgba(0,212,224,.40);
          box-shadow: 0 12px 28px rgba(0,212,224,.15);
        }
        .i-bento-tienda-head {
          display: flex; align-items: center; gap: 12px;
          text-decoration: none;
          color: #fff;
        }
        .i-bento-tienda-head .i-bento-accion-icon {
          width: 38px; height: 38px;
          font-size: 18px;
        }
        .i-bento-tienda-head .i-bento-accion-titulo { font-size: 14px }
        .i-bento-tienda-head .i-bento-accion-sub { font-size: 11.5px }
        .i-bento-tienda-cta {
          display: flex; align-items: center; justify-content: center; gap: 6px;
          background: rgba(0,168,181,.14);
          border: 1px solid rgba(0,168,181,.32);
          border-radius: 10px;
          padding: 8px 12px;
          font-size: 12.5px; font-weight: 800; letter-spacing: .02em;
          color: #fff;
          text-decoration: none;
          transition: background .15s, transform .12s;
          margin-top: 2px;
        }
        .i-bento-tienda-cta:hover {
          background: rgba(0,168,181,.28);
          transform: translateY(-1px);
        }
        .i-bento-tienda-cta span { color: var(--cl-teal); font-weight: 900 }

        @media (max-width: 480px) {
          .i-bento-bonif { padding: 16px 18px }
          .i-bento-bonif-titulo { font-size: 18px }
          .i-bento-bonif-prom-valor { font-size: 18px }
          .i-bento-bonif-meses { font-size: 11px }
          .i-bento-metrica { padding: 12px 14px }
          .i-bento-metrica-valor { font-size: 18px }
        }


        /* Banner falta pagar */
        .i-pago-pendiente {
          display: flex; align-items: center; gap: 14px;
          padding: 16px 18px;
          margin-bottom: 14px;
          background: linear-gradient(135deg, #fffbeb, #fef3c7);
          border: 1.5px solid #f59e0b;
          border-radius: 14px;
          text-decoration: none;
          color: #78350f;
          transition: transform .12s, box-shadow .12s;
        }
        .i-pago-pendiente:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(245,158,11,.18) }
        .i-pago-pendiente-icon { font-size: 26px; flex-shrink: 0 }
        .i-pago-pendiente-body { flex: 1; display: flex; flex-direction: column; gap: 2px; line-height: 1.4 }
        .i-pago-pendiente-body strong { font-size: 14px; font-weight: 800; color: #92400e }
        .i-pago-pendiente-body span { font-size: 12.5px; color: #b45309 }
        .i-pago-pendiente-cta {
          padding: 9px 16px;
          background: #b45309; color: #fff;
          border-radius: 999px;
          font-size: 13px; font-weight: 800;
          white-space: nowrap; flex-shrink: 0;
        }
        .i-strip {
          display: flex; align-items: center; gap: 16px;
          background: linear-gradient(135deg, var(--cl-navy), var(--cl-navy-2));
          color: #fff; padding: 18px; border-radius: 18px;
          text-decoration: none;
          box-shadow: 0 10px 30px rgba(15,31,61,.18);
          transition: transform .15s, box-shadow .15s;
        }
        .i-strip-meta-inline { font-size: 12px; opacity: .8; margin-top: 6px; display: flex; gap: 6px; flex-wrap: wrap }

        /* Tu cuenta — panel glass dark con cards internas glass más oscuras */
        .i-cuenta {
          background: var(--cl-card); border: 1px solid var(--cl-line);
          border-radius: 18px; padding: 16px 18px;
          display: flex; flex-direction: column; gap: 10px;
        }
        .i-cuenta-title { font-size: 13px; font-weight: 900; color: var(--cl-text); letter-spacing: .02em }
        .i-cuenta-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; flex: 1 }
        .i-cuenta-card {
          background: rgba(0,0,0,.20);
          border: 1px solid var(--cl-line); border-radius: 12px;
          padding: 12px; display: flex; flex-direction: column; gap: 6px;
          text-decoration: none; color: var(--cl-text);
          transition: border-color .15s, transform .15s, background .15s;
          min-width: 0;
        }
        .i-cuenta-card:hover { border-color: var(--cl-teal); background: rgba(0,212,224,.05); transform: translateY(-1px) }
        .i-cuenta-icon {
          width: 36px; height: 36px; border-radius: 10px;
          background: rgba(0,212,224,.10); border: 1px solid rgba(0,212,224,.18);
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; flex-shrink: 0;
        }
        .i-cuenta-label { font-size: 9.5px; color: var(--cl-muted); font-weight: 800; letter-spacing: .08em; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis }
        .i-cuenta-val { font-size: 20px; font-weight: 900; color: var(--cl-text); letter-spacing: -.02em; line-height: 1.1 }
        .i-cuenta-sub { font-size: 11px; color: var(--cl-muted); line-height: 1.3 }
        .i-cuenta-foot { font-size: 11px; color: var(--cl-muted); padding-top: 8px; border-top: 1px dashed var(--cl-line); letter-spacing: .02em }
        .i-cuenta-foot b { color: var(--cl-text); font-weight: 800 }
        .i-strip:hover { transform: translateY(-2px); box-shadow: 0 14px 36px rgba(15,31,61,.22) }
        .i-strip-cta { background: linear-gradient(135deg, var(--cl-blue), var(--cl-teal)) }
        .i-strip-icon { font-size: 32px }
        .i-strip-info { flex: 1 }
        .i-strip-label { font-size: 10px; font-weight: 800; opacity: .7; letter-spacing: .1em; text-transform: uppercase }
        .i-strip-title { font-size: 18px; font-weight: 900; margin-top: 4px; letter-spacing: -.01em }
        .i-strip-sub { font-size: 13px; opacity: .85; margin-top: 4px }
        .i-strip-cta-btn {
          display: inline-flex; align-items: center;
          background: var(--cl-teal); color: var(--cl-text);
          padding: 9px 16px; border-radius: 999px;
          font-size: 12px; font-weight: 800;
          flex-shrink: 0;
        }
        .i-strip-cta .i-strip-cta-btn { background: #fff; color: var(--cl-blue) }

        .i-shortcuts { display: grid; grid-template-columns: 1fr 1fr; gap: 10px }
        .i-shortcut {
          background: var(--cl-card); border: 1px solid var(--cl-line);
          padding: 14px 16px; border-radius: 14px;
          display: flex; align-items: center; gap: 14px;
          text-decoration: none; color: var(--cl-text);
          transition: transform .15s, border-color .15s, box-shadow .15s, background .15s;
        }
        .i-shortcut:hover { transform: translateY(-2px); border-color: var(--cl-teal); background: var(--cl-card-hover); box-shadow: 0 8px 20px rgba(0,212,224,.08) }
        /* La Tienda Limpack — más compacta (Nico 04-jun: '50% del tamaño') */
        .i-shortcut.feat {
          grid-column: 1 / -1;
          background: linear-gradient(135deg, var(--cl-bg-2), var(--cl-navy));
          color: #fff; border: 1px solid rgba(0,212,224,.20);
          padding: 14px 18px;
          box-shadow: 0 8px 20px rgba(0,212,224,.14);
        }
        .i-shortcut.feat:hover { box-shadow: 0 12px 28px rgba(0,212,224,.22); border-color: rgba(0,212,224,.35) }
        .i-sc-emoji { font-size: 26px; flex-shrink: 0 }
        .i-shortcut.feat .i-sc-emoji { font-size: 28px }
        .i-sc-body { flex: 1 }
        .i-sc-title { font-size: 14px; font-weight: 800; color: var(--cl-text) }
        .i-shortcut.feat .i-sc-title { color: #fff; font-size: 16px; letter-spacing: -.01em }
        .i-sc-sub { font-size: 12.5px; color: var(--cl-muted); line-height: 1.45; margin-top: 2px }
        .i-shortcut.feat .i-sc-sub { color: rgba(255,255,255,.80); font-size: 12.5px; margin-top: 3px }

        .i-section { font-size: 15px; font-weight: 900; color: var(--cl-text); letter-spacing: -.01em; margin: 18px 0 10px }

        /* Grid auto-fit para Tienda + Vecinos + Tip — Nico 08-jul-2026:
           'los cards no aprovechan el ancho en desktop'. Auto-fit se ajusta solo:
           Mobile: 1 col apilado. Tablet: 2 cols cuando cabe. Desktop grande: 3 cols. */
        .i-secundarios-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 14px;
          margin-top: 18px;
          align-items: stretch;
        }
        @media (min-width: 900px) {
          .i-secundarios-grid {
            gap: 18px;
          }
        }
        .i-tip-wrap { display: flex; flex-direction: column; gap: 10px }
        .i-tip-h2 {
          font-size: 15px; font-weight: 900; color: var(--cl-text);
          letter-spacing: -.01em; margin: 0;
        }

        /* Card Descuento Colaborativo — glass verde sobre dark.
           margin-top removido: ahora va dentro de .i-secundarios-grid. */
        .i-vecinos-card {
          background: rgba(16,185,129,.08);
          border: 1px solid rgba(16,185,129,.25);
          border-radius: 16px;
          padding: 18px;
          display: flex; align-items: flex-start; gap: 14px;
          height: 100%;  /* iguala altura con el Tip al lado en desktop */
        }
        .i-vecinos-emoji {
          font-size: 36px; line-height: 1; flex-shrink: 0;
        }
        .i-vecinos-body { flex: 1; min-width: 0 }
        .i-vecinos-pill {
          display: inline-block;
          font-size: 10px; font-weight: 800; padding: 3px 9px;
          background: #10b981; color: #022c22; border-radius: 999px;
          text-transform: uppercase; letter-spacing: .04em;
          margin-bottom: 10px; white-space: nowrap;
          max-width: 100%;
        }
        .i-vecinos-title {
          font-size: 18px; font-weight: 900; color: #6ee7b7;
          letter-spacing: -.01em; margin-bottom: 6px;
        }
        .i-vecinos-desc {
          font-size: 13.5px; color: #a7f3d0; line-height: 1.5;
          margin-bottom: 14px;
        }
        .i-vecinos-desc strong { color: #6ee7b7 }
        .i-vecinos-actions { display: flex; gap: 8px; flex-wrap: wrap }
        .i-vecinos-btn {
          padding: 10px 16px; border-radius: 10px;
          font-size: 13px; font-weight: 800;
          text-decoration: none; white-space: nowrap;
          transition: transform .12s, box-shadow .15s;
        }
        .i-vecinos-btn:hover { transform: translateY(-1px) }
        .i-vecinos-btn.primary {
          background: #10b981; color: #022c22;
        }
        .i-vecinos-btn.primary:hover { background: #34d399 }
        .i-vecinos-btn.wa {
          background: #25d366; color: #022c22;
          box-shadow: 0 2px 8px rgba(37,211,102,.25);
        }
        .i-vecinos-btn.wa:hover { background: #34e07a; box-shadow: 0 4px 12px rgba(37,211,102,.35) }
        @media (max-width: 480px) {
          .i-vecinos-card { padding: 14px; gap: 10px }
          .i-vecinos-emoji { font-size: 28px }
          .i-vecinos-title { font-size: 16px }
          .i-vecinos-desc { font-size: 13px }
          .i-vecinos-btn { padding: 9px 12px; font-size: 12.5px }
        }
        .i-trust { margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--cl-line) }

        /* Bonificación 3 meses — glass cyan sobre dark */
        .i-bonif {
          margin-top: 12px;
          background: rgba(8,145,178,.10);
          border: 1px solid rgba(34,211,238,.30);
          border-radius: 16px;
          padding: 18px 20px;
        }
        .i-bonif-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 8px }
        .i-bonif-tag { font-size: 13px; font-weight: 800; color: #67e8f9; letter-spacing: -.01em }
        .i-bonif-mes { font-size: 11px; font-weight: 800; color: #022c22; background: #22d3ee; padding: 4px 10px; border-radius: 999px; letter-spacing: .04em; text-transform: uppercase }
        .i-bonif-explain { font-size: 13px; color: #a5f3fc; line-height: 1.55; margin-bottom: 14px }
        .i-bonif-explain b { color: #ecfeff; font-weight: 800 }
        .i-bonif-bar { background: rgba(14,116,144,.25); height: 8px; border-radius: 999px; overflow: hidden }
        .i-bonif-bar-fill { background: linear-gradient(90deg, #06b6d4, #00d4e0); height: 100%; transition: width .3s }
        .i-bonif-meses { display: flex; justify-content: space-between; gap: 8px; margin-top: 8px; font-size: 11px; font-weight: 700; color: var(--cl-muted); flex-wrap: wrap }
        .i-bonif-meses .on { color: #67e8f9 }
        .i-bonif-resumen {
          margin-top: 12px; padding: 10px 12px;
          background: rgba(0,0,0,.20); border: 1px solid rgba(34,211,238,.18);
          border-radius: 10px; font-size: 12px; color: #a5f3fc; line-height: 1.5;
        }
        .i-bonif-resumen b { color: #ecfeff; font-weight: 800 }

        .i-mini {
          background: var(--cl-card); border: 1px solid var(--cl-line);
          padding: 16px; border-radius: 14px; text-decoration: none;
          color: var(--cl-text); transition: border-color .15s, transform .15s, background .15s;
          display: flex; gap: 12px; align-items: center;
          margin-bottom: 12px;
        }
        .i-mini:hover { border-color: var(--cl-teal); background: var(--cl-card-hover); transform: translateY(-1px) }
        .i-mini-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: rgba(0,212,224,.10); border: 1px solid rgba(0,212,224,.18);
          display: flex; align-items: center; justify-content: center;
          font-size: 22px; flex-shrink: 0;
        }
        .i-mini-body { flex: 1; min-width: 0 }
        .i-mini-label { font-size: 10px; color: var(--cl-muted); font-weight: 800; letter-spacing: .08em; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis }
        .i-mini-val { font-size: 22px; font-weight: 900; color: var(--cl-text); margin-top: 2px; letter-spacing: -.02em }
        .i-mini-sub { font-size: 11px; color: var(--cl-muted); margin-top: 2px }

        /* Burbujas con 2 datos divididos */
        .i-mini-split-card { align-items: stretch }
        .i-mini-split-card .i-mini-icon { align-self: flex-start; margin-top: 4px }
        .i-mini-split { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
        .i-mini-half { padding-right: 12px; border-right: 1px solid var(--cl-line) }
        .i-mini-half:last-child { border-right: 0; padding-right: 0; padding-left: 4px }
        .i-mini-half .i-mini-val { font-size: 18px }
        .i-mini-iva { font-size: 10px; color: var(--cl-muted); margin-top: 8px; padding-top: 8px; border-top: 1px dashed var(--cl-line); font-weight: 700; letter-spacing: .04em; text-transform: uppercase }

        /* Tip Limpack — glass amarillo sobre dark */
        .i-tip {
          background: rgba(251,191,36,.10);
          border: 1px solid rgba(251,191,36,.25);
          border-radius: 14px; padding: 16px;
          display: flex; gap: 12px; align-items: flex-start;
        }
        .i-tip-icon { font-size: 28px; flex-shrink: 0; line-height: 1 }
        .i-tip-text { font-size: 13.5px; color: #fde68a; line-height: 1.55; flex: 1; min-width: 0; word-wrap: break-word; overflow-wrap: anywhere }
        .i-tip-text b { color: #fef3c7; font-weight: 800 }
        .i-tip-prod {
          text-decoration: none; cursor: pointer;
          transition: transform .15s, box-shadow .15s;
          align-items: center; padding: 14px;
        }
        .i-tip-prod:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(252,211,77,.4) }
        .i-tip-prod-img {
          width: 56px; height: 70px; border-radius: 10px; flex-shrink: 0;
          background: rgba(255,255,255,.4); overflow: hidden;
          box-shadow: 0 4px 12px rgba(120,53,15,.12);
        }
        .i-tip-prod-img img { width: 100%; height: 100%; object-fit: cover; display: block }
        .i-section-tip { margin-top: 28px }
        @media (min-width: 900px) {
          .i-tip-prod-img { width: 84px; height: 105px }
          .i-tip-wide { padding: 22px 26px; gap: 22px; align-items: center }
          .i-tip-wide .i-tip-icon { font-size: 38px }
          .i-tip-wide .i-tip-text { font-size: 15px; line-height: 1.6 }
        }

        @media (min-width: 900px) {
          .i-wrap { padding: 32px 44px }
          .i-hero { padding: 0 0 18px }
          .i-greet { font-size: 14px }
          /* H1 desktop reducido de 44px → 34px (Nico 04-jun) */
          .i-hello { font-size: 34px }
          .i-subhello { font-size: 14px; margin-top: 6px }

          .i-top-grid { grid-template-columns: 1.3fr 1fr; gap: 18px; align-items: start; margin-bottom: 4px }
          .i-strip { padding: 16px 20px; gap: 16px; margin-bottom: 0 }
          .i-strip-icon { font-size: 30px }
          .i-strip-label { font-size: 10px }
          .i-strip-title { font-size: 20px }
          .i-strip-sub { font-size: 13px }
          .i-strip-meta-inline { font-size: 13px; gap: 8px }
          .i-strip-cta-btn { padding: 10px 18px; font-size: 12px }

          .i-cuenta { padding: 18px 20px; gap: 14px }
          .i-cuenta-title { font-size: 14px }
          .i-cuenta-icon { width: 40px; height: 40px; font-size: 20px }
          .i-cuenta-val { font-size: 22px }
          /* Section title desktop: más compacto (Nico 04-jun) */
          .i-section { font-size: 16px; margin: 22px 0 10px }
          .i-shortcuts { gap: 14px }
          .i-shortcut { padding: 16px 18px }
          /* La Tienda Limpack desktop: padding más chico, font moderado */
          .i-shortcut.feat { padding: 18px 22px }
          .i-sc-title { font-size: 15px }
          .i-shortcut.feat .i-sc-title { font-size: 18px }
          .i-sc-sub { font-size: 12.5px }
          .i-shortcut.feat .i-sc-sub { font-size: 13px }

          .i-mini { padding: 18px }
          .i-mini-icon { width: 48px; height: 48px; font-size: 24px }
          .i-mini-val { font-size: 26px }
          .i-mini-sub { font-size: 12px }
          .i-tip { padding: 16px 18px }
          .i-tip-text { font-size: 13.5px }
          .i-tip-prod-img { width: 64px; height: 80px }
        }
      `}</style>
    </ClienteLayout>
  )
}
